import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { API_URL } from "@/config/company";

const JOB_TYPES = ["Full-time", "Part-time", "Contract", "Internship", "Consulting Engagement"];
const EMPTY_JOB = { title: "", location: "", job_type: "Full-time", description: "" };

function JobsAdmin({ adminKey, authed }) {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(EMPTY_JOB);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/jobs`);
      setJobs(r.ok ? await r.json() : []);
    } catch {
      setJobs([]);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const r = await fetch(`${API_URL}/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Key": adminKey },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setMessage({ ok: true, text: "Vacancy published to the Careers page." });
      setForm(EMPTY_JOB);
      load();
    } catch {
      setMessage({ ok: false, text: "Could not publish. Check the details and try again." });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      const r = await fetch(`${API_URL}/jobs/${id}`, { method: "DELETE", headers: { "X-Admin-Key": adminKey } });
      if (!r.ok) throw new Error();
      load();
    } catch {
      setMessage({ ok: false, text: "Could not remove the vacancy." });
    }
  };

  if (!authed) return null;

  const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-corp focus:outline-none";

  return (
    <div className="mt-14" data-testid="admin-jobs-section">
      <h2 className="font-heading text-2xl font-bold text-navy">Job Postings</h2>
      <p className="mt-1.5 text-sm text-muted">Publish or remove vacancies on the Careers page.</p>

      <form onSubmit={create} className="mt-6 grid sm:grid-cols-2 gap-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-job-form">
        <div>
          <label htmlFor="job-title" className="block text-sm font-semibold text-ink mb-1.5">Job title</label>
          <input id="job-title" required minLength={2} className={inputCls} data-testid="admin-job-title"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label htmlFor="job-location" className="block text-sm font-semibold text-ink mb-1.5">Location</label>
          <input id="job-location" required minLength={2} placeholder="e.g. Lagos (Hybrid)" className={inputCls} data-testid="admin-job-location"
            value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <div>
          <label htmlFor="job-type" className="block text-sm font-semibold text-ink mb-1.5">Type</label>
          <select id="job-type" className={inputCls} data-testid="admin-job-type"
            value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
            {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="job-description" className="block text-sm font-semibold text-ink mb-1.5">Role description</label>
          <textarea id="job-description" required minLength={10} rows={5} className={inputCls} data-testid="admin-job-description"
            placeholder="Responsibilities, requirements and how to apply…"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="sm:col-span-2 flex items-center gap-4">
          <button type="submit" disabled={busy} data-testid="admin-job-publish-btn"
            className="rounded-full bg-corp hover:bg-bright disabled:opacity-60 text-white text-sm font-semibold px-6 py-2.5 transition-colors">
            {busy ? "Publishing…" : "Publish Vacancy"}
          </button>
          {message && (
            <p role="status" data-testid="admin-job-message" className={`text-sm ${message.ok ? "text-teal-dark" : "text-red-600"}`}>{message.text}</p>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" data-testid="admin-jobs-list">
        {jobs.length === 0 && <p className="text-sm text-muted">No vacancies published.</p>}
        {jobs.map((job) => (
          <div key={job.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4">
            <div>
              <p className="font-semibold text-ink text-sm">{job.title}</p>
              <p className="text-xs text-muted mt-0.5">{job.location} · {job.job_type}</p>
            </div>
            <button type="button" onClick={() => remove(job.id)} data-testid={`admin-job-delete-${job.id}`}
              className="shrink-0 rounded-full border border-red-300 text-red-600 text-xs font-semibold px-4 py-2 hover:bg-red-50 transition-colors">
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/enquiries`, { headers: { "X-Admin-Key": key } });
      if (!res.ok) throw new Error(res.status === 401 ? "Invalid admin key" : "Request failed");
      setEnquiries(await res.json());
    } catch (err) {
      setEnquiries(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-mist min-h-screen pt-32 pb-20">
      <SEO title="Admin — Enquiries" description="Internal enquiry viewer." path="/admin" />
      <div className="container-site max-w-5xl">
        <h1 className="font-heading text-3xl font-bold text-navy">Site Administration</h1>
        <p className="mt-2 text-sm text-muted">Internal view. Enter the administrator key to load enquiries and manage job postings.</p>
        <form onSubmit={load} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="admin-key-form">
          <label htmlFor="admin-key" className="sr-only">Admin key</label>
          <input
            id="admin-key"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            autoComplete="off"
            required
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm focus:border-corp focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-load-btn"
            className="rounded-full bg-corp hover:bg-bright disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors"
          >
            {loading ? "Loading…" : "Sign In"}
          </button>
        </form>
        {error && <p role="alert" className="mt-4 text-sm text-red-600" data-testid="admin-error">{error}</p>}
        {enquiries && (
          <div className="mt-8" data-testid="admin-enquiries-section">
            <h2 className="font-heading text-2xl font-bold text-navy">Consultation Enquiries</h2>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-enquiries-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-mist text-left">
                    <th className="px-5 py-3 font-semibold text-ink">Received</th>
                    <th className="px-5 py-3 font-semibold text-ink">Name</th>
                    <th className="px-5 py-3 font-semibold text-ink">Company</th>
                    <th className="px-5 py-3 font-semibold text-ink">Service</th>
                    <th className="px-5 py-3 font-semibold text-ink">Contact</th>
                    <th className="px-5 py-3 font-semibold text-ink">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-8 text-center text-muted">No enquiries yet.</td></tr>
                  )}
                  {enquiries.map((enq) => (
                    <tr key={enq.id} className="border-b border-slate-100 align-top">
                      <td className="px-5 py-4 text-muted whitespace-nowrap">{new Date(enq.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4 text-ink font-medium">{enq.full_name}</td>
                      <td className="px-5 py-4 text-muted">{enq.company}</td>
                      <td className="px-5 py-4 text-muted">{enq.service}</td>
                      <td className="px-5 py-4 text-muted">{enq.work_email}{enq.phone ? ` · ${enq.phone}` : ""}</td>
                      <td className="px-5 py-4 text-muted max-w-xs whitespace-pre-wrap">{enq.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <JobsAdmin adminKey={key} authed={enquiries !== null} />
      </div>
    </section>
  );
}
